import { CategoryNestedSelectDto } from '@@types/api.types';
import { SankeyModal } from '@components/shared/sankey-modal.component';
import { type SankeyProps } from '@components/shared/sankey.component';
import { TechnologyEffectsSankeyComponent } from '@components/shared/technology-effects-sankey-chart.component';
import { Tile } from '@components/shared/tile.component';
import { useMissionAssessmentLatestByCompanyQueryOptions } from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { ZoomInIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

type TechnologyEffectsTileProps = {
  sankeyProps?: Partial<SankeyProps>;
};

export const TechnologyEffectsTile = (props: TechnologyEffectsTileProps) => {
  const { sankeyProps } = props;
  const latestMissionAssessmentsQueryOptions = useMissionAssessmentLatestByCompanyQueryOptions();
  const { data: missionAssessments, isLoading, error } = useQuery(latestMissionAssessmentsQueryOptions);

  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);

  // Transform mission assessments into the format expected by TechnologyEffectsSankeyComponent
  const sankeyData = useMemo(() => {
    if (!missionAssessments || missionAssessments.length === 0) {
      return [];
    }

    return missionAssessments
      .filter((assessment) => {
        const hasCategory = !!assessment.technology.category;
        const hasEffects = assessment.effects?.length > 0;
        return hasCategory && hasEffects;
      })
      .map((assessment) => ({
        // Type assertion since we know category exists from the filter above
        category: assessment.technology.category as CategoryNestedSelectDto,
        effects: assessment.effects,
        technology: assessment.technology,
      }));
  }, [missionAssessments]);

  const isEmpty = !sankeyData || sankeyData.length === 0;
  const hasError = !!error;

  // Build technology options for the modal
  const technologyOptions = useMemo(() => {
    // Only include technologies that still have effects (sankeyData already filtered)
    const unique: Record<string, string> = {};
    sankeyData.forEach((d) => {
      if (!unique[d.technology.id]) {
        unique[d.technology.id] = d.technology.name;
      }
    });
    return Object.entries(unique).map(([id, name]) => ({ id, name }));
  }, [sankeyData]);

  if (isEmpty && !isLoading) {
    return null;
  }

  return (
    <>
      <Tile
        className="min-h-[300px]"
        emptyState={{
          title: hasError ? 'Error Loading Data' : 'No Effects Available',
        }}
        header={{
          iconButton: {
            icon: <ZoomInIcon />,
            onClick: () => setIsOpenFullScreenModal(true),
          },
          title: 'Technology Effects',
        }}
        hoverReveal
        isEmpty={isEmpty}
        isLoading={isLoading}
      >
        {!isEmpty && (
          <TechnologyEffectsSankeyComponent
            data={sankeyData}
            sankeyProps={{
              ...sankeyProps,
              onOpenFullScreen: () => setIsOpenFullScreenModal(true),
            }}
          />
        )}
      </Tile>

      {isOpenFullScreenModal && (
        <SankeyModal
          isOpen={isOpenFullScreenModal}
          onClose={() => setIsOpenFullScreenModal(false)}
          title="Technology Effects"
          technologyOptions={technologyOptions}
        >
          {({ settings, selectedTechnologyId }) => (
            <TechnologyEffectsSankeyComponent
              data={sankeyData}
              settings={settings}
              technologyId={selectedTechnologyId}
            />
          )}
        </SankeyModal>
      )}
    </>
  );
};
